import SEO from 'components/SEO'
import { useMemo, useState, type ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@cannect/lib/utils'
import { FAQ_CATEGORIES, FAQ_ITEMS, FAQ_PATH, FAQ_SEARCH_SUGGESTIONS, PAGE_PATH } from '../constants'
import '../styles.css'
import { SUPPORT_WHATSAPP_URL } from '../trail/copy'
import { buildTrailPath } from '../trail/paths'

const SEO_TITLE = 'Central de dúvidas do Programa de Cuidado para Perda de Peso'
const SEO_DESCRIPTION =
  'Tire suas dúvidas sobre o Programa Cannect de perda de peso: como funciona, equipe, pagamento, medicação e acompanhamento.'

type TFaqItem = (typeof FAQ_ITEMS)[number]

const ALL = 'todas'

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const countLabel = (count: number) => `${count} ${count === 1 ? 'pergunta' : 'perguntas'}`

const SvgIcon = ({ size, stroke = 'currentColor', strokeWidth = 2, children }: SvgIconProps) => (
  <svg
    aria-hidden
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0">
    {children}
  </svg>
)

type SvgIconProps = { size: number; stroke?: string; strokeWidth?: number; children: ReactNode }

// Destaca no título a primeira palavra buscada, como no design.
const HighlightedQuestion = ({ question, word }: { question: string; word: string }) => {
  const index = word ? normalize(question).indexOf(word) : -1
  if (index < 0) return <span>{question}</span>

  return (
    <>
      {question.slice(0, index)}
      <mark className="rounded-[3px] bg-[#F4F7BE] px-px text-primary-1000">
        {question.slice(index, index + word.length)}
      </mark>
      {question.slice(index + word.length)}
    </>
  )
}

const FaqEntry = ({
  item,
  open,
  word,
  category,
  onToggle
}: {
  item: TFaqItem
  open: boolean
  word: string
  category?: string
  onToggle: () => void
}) => (
  <div
    className={cn(
      'rounded-[14px] border border-solid transition-colors duration-200',
      open ? 'border-marketplace-300 bg-white shadow-[0_8px_24px_rgba(59,73,31,.08)]' : 'border-[#E3E3E3] bg-[#FAFAFA]'
    )}>
    <button
      type="button"
      aria-expanded={open}
      aria-controls={`faq-${item.id}`}
      onClick={onToggle}
      className="faq-btn flex min-h-14 w-full cursor-pointer items-center justify-between gap-4 border-0 bg-transparent px-5 py-[18px] text-left font-[inherit] text-base font-medium leading-[1.4] text-primary-1000">
      <span>
        {category ? (
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[1px] text-marketplace-800">
            {category}
          </span>
        ) : null}
        <HighlightedQuestion question={item.question} word={word} />
      </span>
      <span
        aria-hidden
        className={cn(
          'inline-flex size-8 shrink-0 items-center justify-center rounded-full transition-transform duration-200',
          open ? 'rotate-45 bg-marketplace-900 text-white' : 'bg-[#E8EDD9] text-marketplace-900'
        )}>
        <SvgIcon size={16} strokeWidth={2.4}>
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </SvgIcon>
      </span>
    </button>
    {open ? (
      <p id={`faq-${item.id}`} className="faq-answer m-0 px-5 pb-5 text-[15px] leading-[1.65] text-[#5A5555]">
        {item.answer}
      </p>
    ) : null}
  </div>
)

const WeightLossProgramFaq = () => {
  const { search } = useLocation()
  const [query, setQuery] = useState('')
  const [categoryId, setCategoryId] = useState(ALL)
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({})

  const words = normalize(query.trim()).split(/\s+/).filter(Boolean)
  const searching = words.length > 0

  const categoryByItemId = useMemo(() => {
    const map: Record<string, string> = {}
    FAQ_CATEGORIES.forEach((category) =>
      category.items.forEach((item) => {
        map[item.id] = category.label
      })
    )
    return map
  }, [])

  const results = useMemo(
    () =>
      FAQ_ITEMS.filter((item) => {
        const haystack = normalize(`${item.question} ${item.answer}`)
        return words.every((word) => haystack.includes(word))
      }),
    // `words` é derivado de `query`
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query]
  )

  const visibleCategories = FAQ_CATEGORIES.filter((category) => categoryId === ALL || category.id === categoryId)
  const activeCategoryId = searching ? ALL : categoryId
  const found = searching ? results.length : visibleCategories.reduce((sum, category) => sum + category.items.length, 0)

  const toggle = (id: string) => setOpenIds((current) => ({ ...current, [id]: !current[id] }))
  const pickCategory = (id: string) => {
    setCategoryId(id)
    setQuery('')
  }
  const triagePath = buildTrailPath('escolha', undefined, search)

  const chips = [{ id: ALL, label: 'Todas', count: FAQ_ITEMS.length }].concat(
    FAQ_CATEGORIES.map((category) => ({ id: category.id, label: category.label, count: category.items.length }))
  )

  return (
    <div className="wlp">
      <SEO title={SEO_TITLE} description={SEO_DESCRIPTION} canonical={FAQ_PATH} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQ_ITEMS.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: { '@type': 'Answer', text: item.answer }
            }))
          })}
        </script>
      </Helmet>

      <header className="border-0 border-b border-solid border-[#EDECE7] px-5">
        <div className="mx-auto flex min-h-[72px] max-w-[1200px] items-center justify-between gap-4">
          <Link
            to={{ pathname: PAGE_PATH, search }}
            className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-marketplace-900 no-underline">
            <SvgIcon size={18}>
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </SvgIcon>
            <span className="hide-mobile">Voltar para o programa</span>
          </Link>
          <Link
            to="/"
            className="text-lg tracking-[2px] text-primary-800 no-underline"
            aria-label="Cannect — página inicial">
            CANNECT
          </Link>
          <Link
            to={triagePath}
            className="wlp-btn-primary hide-mobile inline-flex min-h-11 items-center justify-center rounded-full bg-marketplace-900 px-5 text-sm font-semibold text-white no-underline">
            Fazer a triagem
          </Link>
          <span className="faq-header-spacer w-11" aria-hidden />
        </div>
      </header>

      <section className="bg-[#F4F6EE] px-5 pb-14 pt-16 max-sm:pb-9 max-sm:pt-10">
        <div className="mx-auto flex max-w-[760px] flex-col gap-4 text-center">
          <span className="text-[13px] font-semibold uppercase tracking-[1.5px] text-marketplace-800">
            Central de dúvidas
          </span>
          <h1 className="t-h1 m-0 text-[46px] font-normal leading-[1.15] text-primary-1000">
            Como podemos <span className="serif text-marketplace-800">ajudar?</span>
          </h1>
          <p className="m-0 mb-2 text-[17px] leading-[1.6] text-[#5A5555]">
            Tudo sobre o Programa de Cuidado para Perda de Peso: como funciona, equipe, pagamento e acompanhamento.
          </p>
          <div className="faq-search flex min-h-16 items-center gap-3 rounded-full border-[1.5px] border-solid border-marketplace-300 bg-white py-0 pl-[22px] pr-2 shadow-[0_10px_30px_rgba(59,73,31,.08)] max-sm:min-h-14 max-sm:pl-[18px]">
            <SvgIcon size={22} stroke="#53632E">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </SvgIcon>
            <input
              id="faq-busca"
              aria-label="Buscar nas dúvidas"
              type="search"
              autoComplete="off"
              placeholder="Digite sua dúvida…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="h-[60px] min-w-0 flex-1 border-0 bg-transparent font-[inherit] text-[17px] text-primary-1000 outline-none max-sm:h-[52px] max-sm:text-base"
            />
            {query ? (
              <button
                type="button"
                aria-label="Limpar busca"
                onClick={() => setQuery('')}
                className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-[#F1F3EA] text-marketplace-900">
                <SvgIcon size={16} strokeWidth={2.4}>
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </SvgIcon>
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1.5 text-[13px] text-[#5A5555]">
            <span>Mais buscados:</span>
            {FAQ_SEARCH_SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => setQuery(suggestion)}
                className="min-h-9 cursor-pointer rounded-full border border-solid border-marketplace-300 bg-white px-3.5 font-[inherit] text-[13px] text-marketplace-900">
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="sticky top-0 z-[2] border-0 border-b border-solid border-[#EDECE7] bg-white/95">
        <div
          role="group"
          aria-label="Filtrar por categoria"
          className="faq-chips mx-auto flex max-w-[1000px] flex-wrap justify-center gap-2 px-5 py-4">
          {chips.map((chip) => {
            const selected = chip.id === activeCategoryId
            return (
              <button
                key={chip.id}
                type="button"
                aria-pressed={selected}
                onClick={() => pickCategory(chip.id)}
                className={cn(
                  'inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-2 rounded-full border border-solid px-4 font-[inherit] text-sm font-medium transition-colors duration-200',
                  selected
                    ? 'border-marketplace-900 bg-marketplace-900 text-white'
                    : 'border-marketplace-300 bg-white text-primary-1000'
                )}>
                {chip.label}
                <span
                  className={cn(
                    'rounded-full px-2 py-px text-xs',
                    selected ? 'bg-marketplace-800 text-white' : 'bg-[#EEF2E6] text-marketplace-800'
                  )}>
                  {chip.count}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      <main className="px-5 pb-8 pt-10">
        <div className="mx-auto flex max-w-[820px] flex-col gap-11">
          {searching ? (
            <div className="-mb-6 flex flex-wrap items-center justify-between gap-3">
              <p aria-live="polite" className="m-0 text-[15px] text-primary-1000">
                {found === 1 ? '1 resultado' : `${found} resultados`} para “{query.trim()}”
              </p>
              <button
                type="button"
                onClick={() => setQuery('')}
                className="min-h-9 cursor-pointer rounded-full border border-solid border-marketplace-300 bg-white px-3.5 font-[inherit] text-[13px] text-marketplace-900">
                Limpar busca
              </button>
            </div>
          ) : null}

          {searching ? (
            <div className="flex flex-col gap-2.5">
              {results.map((item) => (
                <FaqEntry
                  key={item.id}
                  item={item}
                  open={Boolean(openIds[item.id])}
                  word={words[0]}
                  category={categoryByItemId[item.id]}
                  onToggle={() => toggle(item.id)}
                />
              ))}
            </div>
          ) : (
            visibleCategories.map((category) => (
              <section key={category.id} className="flex flex-col gap-3.5" aria-labelledby={`cat-${category.id}`}>
                <div className="flex items-baseline justify-between gap-3 border-0 border-b border-solid border-[#EDECE7] pb-2.5">
                  <h2 id={`cat-${category.id}`} className="m-0 text-[22px] font-medium text-primary-1000">
                    {category.label}
                  </h2>
                  <span className="text-[13px] text-[#5A5555]">{countLabel(category.items.length)}</span>
                </div>
                <div className="flex flex-col gap-2.5">
                  {category.items.map((item) => (
                    <FaqEntry
                      key={item.id}
                      item={item}
                      open={Boolean(openIds[item.id])}
                      word=""
                      onToggle={() => toggle(item.id)}
                    />
                  ))}
                </div>
              </section>
            ))
          )}

          {found === 0 ? (
            <div className="flex flex-col items-center gap-2.5 rounded-2xl border border-dashed border-marketplace-300 bg-[#FAFBF6] px-6 py-9 text-center">
              <SvgIcon size={28} stroke="#53632E" strokeWidth={1.8}>
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
                <path d="M8.5 11h5" />
              </SvgIcon>
              <span className="text-[17px] font-semibold text-primary-1000">Nenhuma pergunta encontrada</span>
              <span className="max-w-[400px] text-sm leading-[1.55] text-[#5A5555]">
                Tente outra palavra, como “consulta” ou “cancelar”, ou fale com a nossa equipe.
              </span>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-6 rounded-[28px] bg-primary-1000 p-10 max-sm:rounded-[22px] max-sm:px-[22px] max-sm:py-7">
            <div className="flex max-w-[460px] flex-col gap-2">
              <span className="text-2xl leading-[1.25] text-white">
                Ainda ficou com <span className="serif text-marketplace-300">dúvida?</span>
              </span>
              <span className="text-[15px] leading-[1.6] text-[#E2E7C8]">
                Nossa equipe está disponível para conversar sobre o programa antes de você decidir.
              </span>
            </div>
            <a
              href={SUPPORT_WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[54px] items-center justify-center gap-2.5 rounded-full bg-marketplace-300 px-[26px] text-[15px] font-semibold text-primary-1000 no-underline max-sm:w-full">
              <SvgIcon size={18}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </SvgIcon>
              Falar com a equipe
            </a>
          </div>
        </div>
      </main>

      <footer className="border-0 border-t border-solid border-[#EDECE7] px-5 py-6">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-3 text-xs leading-[1.6] text-[#5A5555]">
          <Link
            to="/"
            className="text-lg tracking-[2px] text-primary-800 no-underline"
            aria-label="Cannect — página inicial">
            CANNECT
          </Link>
          <span>Este conteúdo é informativo e não substitui a avaliação médica.</span>
        </div>
      </footer>
    </div>
  )
}

export { WeightLossProgramFaq }
export default WeightLossProgramFaq
